import React from 'react';
import ChatTopBarSection from './ChatTopBarSection.jsx';
import ChatBodySection from './ChatBodySection.jsx';
import SettingsManagementPanel from '../../../capabilities/SettingsManagementPanel.jsx';

const resolveActiveSessionId = (viewModel) => (
  String(viewModel.sessionList.activeSessionId ?? '').trim()
);
const resolveSessionDigestText = (activeSession) => {
  if (!activeSession) return '';
  return `当前会话：${String(activeSession.preview || activeSession.title || '').trim()}`;
};
const shouldShowProjectCollection = (hasProject, activeSessionId) => (
  Boolean(hasProject && !activeSessionId)
);
const shouldShowWorkspaceTabSwitch = (showProjectCollection, isCompactLayout) => (
  Boolean(!showProjectCollection && !isCompactLayout)
);
const isWorkspaceBootPending = (workspaceSuspension = {}) => (
  [workspaceSuspension.active === true, workspaceSuspension.operationKey === 'workspace_boot'].every(Boolean)
);

const WorkspaceLoadingHeaderFrame = ({ isCompactLayout }) => (
  <div
    aria-hidden="true"
    className={`flare-workspace-topbar flare-workspace-loading-header-frame${isCompactLayout ? ' is-compact' : ''}`}
  >
    <span className="flare-workspace-loading-header-mark" />
    <span className="flare-workspace-loading-header-title" />
    <span className="flare-workspace-loading-header-action" />
  </div>
);
const WorkspaceLoadingHeader = ({ visible, isCompactLayout }) => (
  visible ? <WorkspaceLoadingHeaderFrame isCompactLayout={isCompactLayout} /> : null
);

export default function MainPane({
  themeTokens,
  hasProject,
  viewModel,
  projectSlot,
  projectNameEditing,
  projectNameDraft,
  setProjectNameDraft,
  handleProjectNameSave,
  handleProjectNameCancel,
  projectDisplayName,
  handleProjectNameStartEdit,
  activeWorkspaceTab,
  setActiveWorkspaceTab,
  skillManagement,
  modeManagement,
  resolvedProductName,
  resolvedProductTag,
  resolvedUILabels,
  activeSession,
  knowledgeHubPopoverContent,
  knowledgeHubPopoverOpen,
  setKnowledgeHubPopoverOpen,
  handleOpenKnowledgeHub,
  canvasAvailable = false,
  showCanvasPanel,
  canvasFullscreenOpen,
  handleToggleWorkspacePanel,
  handleCloseWorkspacePanel,
  projectItems,
  actionGuards,
  composerNode,
  contentMaxWidth,
  handleOpenSourcePicker,
  handleRemoveSource,
  handleRetrySource,
  handleViewSourceDetail,
  sourceActionError,
  sourceItems,
  sourceRemovingId,
  sourceSyncLoading,
  sourceUploadLoading,
  canvasPanelNode,
  renderedTimelineItems,
  handleUICardAction,
  handleWorkflowOperationAction,
  generativeRegistry,
  canExpandScenarios,
  showAllScenarios,
  setShowAllScenarios,
  visibleScenarios,
  recommendationPanelDismissed,
  recommendationPanelTitle,
  handleScenarioCardClick,
  isCompactLayout,
  showSidebarMenuButton,
  handleOpenSidebarMenu,
  mobileHeaderNode,
}) {
  const projectSearch = { projects: viewModel.sessionList.projectSearch }[activeWorkspaceTab];
  const activeSessionId = resolveActiveSessionId(viewModel);
  const showProjectCollection = shouldShowProjectCollection(hasProject, activeSessionId);
  const showWorkspaceTabSwitch = shouldShowWorkspaceTabSwitch(showProjectCollection, isCompactLayout);
  const resolvedSessionDigestText = resolveSessionDigestText(activeSession);
  const workspaceBootPending = isWorkspaceBootPending(viewModel.workspaceSuspension);
  const showWorkspaceLoadingHeader = [workspaceBootPending, !hasProject].every(Boolean);
  // This pane owns header placement; the shell only supplies its navigation slot.
  const showProjectHeader = hasProject && activeWorkspaceTab !== 'projects';
  const handleBackToProject = () => {
    setActiveWorkspaceTab?.('chats');
    viewModel.sessionList.onProjectSelect?.(projectSlot);
  };
  if (activeWorkspaceTab === 'skills') {
    return (
      <div className="flare-chat-standalone-main-pane" style={{ background: themeTokens.surfaceBg, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {mobileHeaderNode}
        <SettingsManagementPanel skillManagement={skillManagement} modeManagement={modeManagement} />
      </div>
    );
  }
  return (
    <div
      className="flare-chat-standalone-main-pane"
      style={{
        background: themeTokens.surfaceBg,
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        minHeight: 0,
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      <WorkspaceLoadingHeader visible={showWorkspaceLoadingHeader} isCompactLayout={isCompactLayout} />
      {showProjectHeader ? (
        <div
          className={activeSessionId ? 'flare-staged-entry' : undefined}
          key={activeSessionId || 'project-collection'}
          style={{
            '--flare-entry-stage': 0,
            flex: '0 0 auto',
            position: 'relative',
            zIndex: 'var(--flare-mobile-layer-topbar, auto)',
          }}
        >
          <ChatTopBarSection
          hasConversationStarted={viewModel.timeline.items.length > 0}
          projectSlot={projectSlot}
          projectNameEditing={projectNameEditing}
          projectNameDraft={projectNameDraft}
          setProjectNameDraft={setProjectNameDraft}
          handleProjectNameSave={handleProjectNameSave}
          handleProjectNameCancel={handleProjectNameCancel}
          resolvedProjectDisplayName={projectDisplayName}
          handleProjectNameStartEdit={handleProjectNameStartEdit}
          activeWorkspaceTab={activeWorkspaceTab}
          setActiveWorkspaceTab={setActiveWorkspaceTab}
          themeTokens={themeTokens}
          resolvedUILabels={resolvedUILabels}
          hasSessionDigest={Boolean(activeSession)}
          resolvedSessionDigest={resolvedSessionDigestText}
          knowledgeHubPopoverContent={knowledgeHubPopoverContent}
          knowledgeHubPopoverOpen={knowledgeHubPopoverOpen}
          handleKnowledgeHubOpenChange={setKnowledgeHubPopoverOpen}
          handleOpenKnowledgeHub={handleOpenKnowledgeHub}
          workspaceOpenButtonVisible={canvasAvailable}
          showCanvasPanel={showCanvasPanel}
          handleToggleWorkspacePanel={handleToggleWorkspacePanel}
          isCompactLayout={isCompactLayout}
          showSidebarMenuButton={showSidebarMenuButton}
          handleOpenSidebarMenu={handleOpenSidebarMenu}
          showWorkspaceTabSwitch={showWorkspaceTabSwitch}
          onBackToProject={handleBackToProject}
          />
        </div>
      ) : mobileHeaderNode}

      <ChatBodySection
        hasProject={hasProject}
        activeSessionId={activeSessionId}
        workspaceSuspension={viewModel.workspaceSuspension}
        workspaceSurface={viewModel.sessionList.workspaceSurface}
        activeWorkspaceTab={activeWorkspaceTab}
        setActiveWorkspaceTab={setActiveWorkspaceTab}
        themeTokens={themeTokens}
        projectItems={projectItems}
        collectionScopeKey={viewModel.sessionList.collectionScopeKey}
        projectSlot={projectSlot}
        resolvedProductName={resolvedProductName}
        resolvedProductTag={resolvedProductTag}
        resolvedUILabels={resolvedUILabels}
        handleCreateProject={actionGuards.handleCreateProject}
        createProjectDisabled={actionGuards.createProjectPending}
        composerNode={composerNode}
        contentMaxWidth={contentMaxWidth}
        handleOpenSourcePicker={handleOpenSourcePicker}
        handleRemoveSource={handleRemoveSource}
        handleRetrySource={handleRetrySource}
        handleViewSourceDetail={handleViewSourceDetail}
        sourceActionError={sourceActionError}
        sourceItems={sourceItems}
        sourceRemovingId={sourceRemovingId}
        sourceSyncLoading={sourceSyncLoading}
        sourceUploadLoading={sourceUploadLoading}
        sessions={viewModel.sessionList.sessions}
        sessionError={actionGuards.sessionPaneError}
        sessionLoading={viewModel.sessionList.loading}
        sessionLoadingMore={viewModel.sessionList.loadingMore}
        sessionHasMore={viewModel.sessionList.hasMore}
        sessionContinuationKey={viewModel.sessionList.sessionContinuationKey}
        sessionSortOrder={viewModel.sessionList.sortOrder}
        projectHasMore={viewModel.sessionList.projectHasMore}
        projectContinuationKey={viewModel.sessionList.projectContinuationKey}
        projectLoading={viewModel.sessionList.projectLoading}
        projectLoadingMore={viewModel.sessionList.projectLoadingMore}
        loadingSessionId={viewModel.sessionList.loadingSessionId}
        runningSessionIds={viewModel.sessionList.runningSessionIds}
        onSelectSession={viewModel.sessionList.onSelectSession}
        onRenameSession={viewModel.sessionList.onRenameSession}
        onArchiveSession={viewModel.sessionList.onArchiveSession}
        onArchiveProject={viewModel.sessionList.onArchiveProject}
        onPinProject={viewModel.sessionList.onPinProject}
        onPinSession={viewModel.sessionList.onPinSession}
        onLoadMoreSessions={viewModel.sessionList.onLoadMore}
        onLoadMoreProjects={viewModel.sessionList.onLoadMoreProjects}
        onRetrySessions={viewModel.sessionList.onRetryLoad}
        onSortSessions={viewModel.sessionList.onSortSessions}
        projectSearch={projectSearch}
        onSearchProjects={viewModel.sessionList.onSearchProjects}
        onProjectSelect={viewModel.sessionList.onProjectSelect}
        onRenameProject={handleProjectNameStartEdit}
        artifactCollection={viewModel.sessionList.artifactCollection}
        skillProjection={skillManagement?.projection}
        showCanvasPanel={showCanvasPanel}
        canvasFullscreenOpen={canvasFullscreenOpen}
        handleCloseWorkspacePanel={handleCloseWorkspacePanel}
        canvasPanelNode={canvasPanelNode}
        streamError={actionGuards.streamError}
        streamLoading={viewModel.timeline.loading}
        messageWindow={viewModel.timeline.messageWindow}
        onLoadOlderMessages={viewModel.timeline.onLoadOlder}
        onStreamRetry={actionGuards.handleRetry}
        renderedTimelineItems={renderedTimelineItems}
        onUICardAction={handleUICardAction}
        onWorkflowOperationAction={handleWorkflowOperationAction}
        registry={generativeRegistry}
        canExpandScenarios={canExpandScenarios}
        currentInputValue={viewModel.composer.value}
        showAllScenarios={showAllScenarios}
        onToggleShowAllScenarios={() => setShowAllScenarios((prev) => !prev)}
        visibleScenarios={visibleScenarios}
        shouldRenderRecommendationPanel={recommendationPanelDismissed !== true}
        recommendationPanelTitle={recommendationPanelTitle}
        onScenarioCardClick={handleScenarioCardClick}
        isCompactLayout={isCompactLayout}
      />
    </div>
  );
}
